import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Icon } from "../utils/icon/icon";
import { SafeHtmlPipe } from "../../pipes/safe-html.pipe";
import { Modal } from "../utils/modal/modal";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { TileTypeEnum } from "src/app/core/interfaces/tile-interface";
import { FavoriteStore } from "src/app/features/store/favorite-store";

@Component({
  selector: "app-card-result",
  imports: [Icon, SafeHtmlPipe, Modal, TranslocoModule],
  templateUrl: "./card-result.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./card-result.scss"],
})
export class CardResult {
  title = input.required<string>();
  id = input<string>();
  text = input.required<string>();
  image = input<string>();
  link = input.required<string>();
  variant = input<"primary" | "secondary" | "tertiary" | "quaternary">(
    "primary"
  );

  isRedirectionExternal = input<boolean>(false);
  modalTextDisclaimer = input<string>();

  private readonly translocoService = inject(TranslocoService);
  modalTextDisclaimerResolved = computed(
    () =>
      this.modalTextDisclaimer() ??
      this.translocoService.translate("components.modal.textDisclaimerDefault")
  );
  isAbleToAddToFavorites = input<boolean>(false);

  private readonly favoriteStore = inject(FavoriteStore);

  isModalOpen = signal<boolean>(false);

  isFavorite = computed(() => {
    const tileId = this.id();
    if (!tileId) {
      return false;
    }
    const isTileFavoriteFn = this.favoriteStore.isTileFavorite();
    return isTileFavoriteFn(tileId);
  });

  isFavoriteLoading = computed(() =>
    this.id() ? this.favoriteStore.isLoading() : false
  );

  constructor() {
    effect(() => {
      document.body.style.overflow = this.isModalOpen() ? "hidden" : "";
    });
  }

  openModal(): void {
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
  }

  completeModal(): void {
    this.closeModal();
    this.openLinkAndRedirect();
  }

  openLinkAndRedirect(): void {
    window.open(this.link(), "_blank");
  }

  handleClick(): void {
    if (this.isRedirectionExternal()) {
      this.openModal();
    } else {
      this.openLinkAndRedirect();
    }
  }

  toggleFavorite(): void {
    if (this.isFavorite()) {
      this.removeFromFavorites();
    } else {
      this.addToFavorites();
    }
  }

  async addToFavorites(): Promise<void> {
    await this.favoriteStore.addTileToFavorites(
      this.id(),
      this.title(),
      this.text(),
      this.link(),
      null,
      this.isRedirectionExternal(),
      TileTypeEnum.Video
    );
  }

  async removeFromFavorites(): Promise<void> {
    await this.favoriteStore.removeTileFromFavorites(this.id());
  }
}
